import { PushErrorNotification, PushSuccessNotification } from "@repo/helpers";
import { KeyedArray } from "@repo/models";
import { Button, Col, Row, useAlertContext } from "@repo/ui";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import AuditApi from "../api/auditApi";
import { usePendingChangesContext } from "../contexts/pendingChangesContext";
import { AuditConfig, AuditConfigDefinition } from "../models";
import AuditTypeSelector from "./auditTypeSelector";
import AuditConfigGrid, { loadConfig } from "./configGrid";
import SingleAuditEditor from "./singleAuditEditor";
import style from "./style.module.scss";
import { Props } from "./types";

export function AuditEditorInternal(props: Props) {
	const { clearAlert, updateAlert } = useAlertContext();
	const [auditConfig, setAuditConfig] = useState<AuditConfig | undefined>(undefined);
	const [savedConfig, setSavedConfig] = useState<AuditConfig | undefined>(undefined);
	const [definition, setDefinition] = useState<AuditConfigDefinition | undefined>(undefined);

	const { pendingChanges, setItemChanged } = usePendingChangesContext();

	const configQuery = useQuery({
		queryKey: ["Configs", props.code],
		queryFn: () => loadConfig(props.code),
	});

	useEffect(() => {
		if (auditConfig) return;
		if (!configQuery.data) return;

		openDefaultConfig(configQuery.data);
	}, [configQuery.data]);

	function openDefaultConfig(configs: KeyedArray<AuditConfig>) {
		const config = configs.get(props.id) || configs.data[0];
		if (auditConfig && config && auditConfig?.id === config?.id) return;

		if (config) {
			openConfig(config);
		} else {
			newConfig();
		}
	}

	async function runAudit() {
		const response = await AuditApi.runAudit(definition?.Code, auditConfig.id);
		if (response === "Audit Started!") PushSuccessNotification(response);
		else PushErrorNotification(response as string);
	}

	function newConfig() {
		setAuditConfig(undefined);
		setSavedConfig(undefined);
		setItemChanged("audit", false);
	}

	const openConfigClicked = (config: AuditConfig) => {
		if (config.id === auditConfig.id) return;
		else if (pendingChanges) askShouldChange(config)("There are pending changes.  Are you sure?");
		else openConfig(config);
	};

	/**
	 * Display an alert asking the user if they are sure they would like to close the lead details modal
	 * @param config
	 */
	const askShouldChange = (config: AuditConfig) => (message: string) => {
		updateAlert({
			message: message,
			onCancel: clearAlert,
			onConfirm: changeConfirmed(config),
			titleText: "Are you sure?",
		});
	};

	/**
	 * Clear out modal values
	 */
	const changeConfirmed = (config: AuditConfig) => () => {
		clearAlert();
		openConfig(config);
	};

	//Need to check if pending changes (with dispatch)
	function openConfig(config: AuditConfig) {
		setAuditConfig(config);
		setSavedConfig(config);
		setItemChanged("audit", false);
	}

	//need list of existing on left
	return (
		<Col className={style.container}>
			<Row>
				<Col md={8}>{renderConfigList()}</Col>
				<Col md={4}>{renderCurrent()}</Col>
			</Row>
		</Col>
	);

	//TODO: handle loading source for ddls (cache here in keyedArray by param name so don't keep fetching)
	function renderCurrent() {
		return (
			<section id="selectedConfig">
				<Col md={12}>
					<Row className={style.padBottom}>
						<SingleAuditEditor
							definition={definition}
							auditConfig={auditConfig}
							setAuditConfig={setAuditConfig}
							savedConfig={savedConfig}
							setSavedConfig={setSavedConfig}
						/>
					</Row>
					<Row>
						<Button onClick={runAudit} disabled={pendingChanges}>
							Run Now
						</Button>
					</Row>
				</Col>
			</section>
		);
	}

	function renderConfigList() {
		return (
			<section id="configSelector" className={style.configSelector}>
				<AuditTypeSelector code={props.code} definition={definition} setDefinition={setDefinition} />
				<AuditConfigGrid {...props} onClickRow={openConfigClicked} definition={definition?.Name} />
				<div className={style.padBottom}></div>
				<Button onClick={newConfig} className={style.addNewBtn}>
					Add New
				</Button>
			</section>
		);
	}
}
